import {
  Button,
  Col,
  Divider,
  Form,
  Progress,
  Row,
  Select,
  Table,
  Tag,
  Input,
  Tooltip,
} from "antd";
import { useEffect, useState } from "react";
import { ArrowUpOutlined } from '@ant-design/icons';
import { HasAccess } from '@permify/react-role';
import { calculateExamResult } from "../../../../services/exam";
import NormalForm from "./NormalForm";

const PsychologyForm = ({
  fromExternal,
  inputs,
  disableForm,
  form,
  admissionPatientExamId,
  roles,
  psychoState,
  setPsycho,
  status,
  removeDocument,
  convertFile,
  convertError,
  isDICOM,
  handleConvertFile,
  imageUrl,
  setImageUrl,
  from,
  countryCode,
  countrySettings,
}) => {
  const [progressState, setProgress] = useState(0);
  const [missingState, setMissing] = useState({});
  const [loadingState, setLoading] = useState(false);
  const isVocalHandicap = inputs.code === 'TEST_INCAPACIDAD_VOCAL';

  useEffect(() => {
    if (inputs.procedures && inputs.procedures.length > 0) {
      setPsycho(
        inputs.procedures
          .reduce((prev, acc) => ({ ...prev, [acc.id]: acc }), {})
      )
    }
  }, [inputs.procedures, setPsycho]);

  useEffect(() => {
    if (inputs?.questions && inputs.questions.length > 0) {
      const formValues = form.getFieldsValue();
      if (formValues.questions) {
        const questions = Object.values(formValues.questions);
        const completedQuestions = questions.filter(question => question && question.toString().trim() !== '');
        const initialProgress = completedQuestions.length * 100 / questions.length;
        setProgress(initialProgress);
      }
    }
  }, [inputs?.questions, form]);

  const fetchCalculator = async () => {
    try {
      const questionResults = form.getFieldsValue().questions
      const response = await calculateExamResult({
        admissionPatientExamId,
        body: {
          form_type: inputs.form_type,
          procedures: inputs.procedures
            .sort((first, second) => first.order - second.order)
            .map(procedure => ({
              id: procedure.id,
              procedure_order: procedure.order
            })),
          questions: inputs.questions
            .sort((first, second) => first.order - second.order)
            .sort((first, second) => first.survey_id - second.survey_id)
            .map(question => ({
              id: question.id,
              answer: questionResults[question.id] || (question.options.length ? question.options.sort((a, b) => a.value - b.value)[0].text : ''),
              questionOptions: question.options,
              survey_code: question.survey_code,
            }))
        },
      });
      return response;
    } catch (error) {
      console.log('Error, ', error);
    }
  }

  const handleCalculator = async () => {
    setLoading(true);
    const response = await fetchCalculator();
    const formValues = Object.keys(form.getFieldsValue()?.procedures);
    const procedureResults = response.reduce((accumulated, procedure) => {
      const oldProcedure = psychoState && psychoState[procedure.id] ?
        psychoState[procedure.id]
        : procedure;

      if (formValues?.includes(procedure.id.toString())) {
        form.setFields([{
          name: ['procedures', oldProcedure.id.toString()],
          value: procedure.result,
        }])
      }
      return {
        ...accumulated,
        [oldProcedure.id]: {
          ...oldProcedure,
          result: procedure.result,
          result_type: procedure.result_type,
        }
      }
    }, {});
    setPsycho(procedureResults);

    const interpretationFields = response.filter(proc => proc.interpretation);
    if (interpretationFields.length) {
      form.setFields(
        interpretationFields.map(proc => ({
          name: ['procedures', proc.id.toString()],
          value: proc.interpretation,
        }))
      );
    }
    setLoading(false);
  }

  const results = () =>
    <Row>
      <Col lg={24} xs={24} className='col-results'>
        <Row justify="center" gutter={[0, 25]}>
          <Col lg={22} xs={22}>
            <h2>Resultados</h2>
          </Col>
          <Col lg={22} xs={22}>
            <Table
              className='cmt-table'
              dataSource={inputs?.procedures?.filter(procedure => procedure.blocked).sort((first, second) => first.order - second.order)}
              pagination={false}
              rowKey='id'
            >
              <Table.Column title="Componente" align='left' width='50%' dataIndex='name' />
              <Table.Column title="Respuesta" align='center' width='50%'
                render={(item) =>
                  <Tag
                    className='cmt-tag'
                  >
                    {(psychoState?.[item.id]?.result || psychoState?.[item.id]?.result === 0) ? psychoState[item.id].result : 'Sin Resultado'}
                  </Tag>
                }
              />
            </Table>
          </Col>
          <Col lg={22} xs={22}>
            <Row justify="end">
              <Col lg={15} xs={24}>
                <HasAccess
                  roles={[
                    'super_usuario', 'equipo_medico', 'equipo_psicologia', 'equipo_medico_externo',
                    ...(isVocalHandicap ? ['doctor', 'direccion_medica'] : [] )
                  ]}
                >
                  <Button
                    className="cmt-button primary"
                    type="primary"
                    onClick={handleCalculator}
                    disabled={
                      disableForm ||
                      (!fromExternal &&
                        !roles.includes('doctor') &&
                        Object.values(psychoState || {}).every(
                          (obj) => obj.medical_visibility === true
                        ))
                    }
                    loading={loadingState}
                  >
                    Calcular resultados
                  </Button>
                </HasAccess>
              </Col>
            </Row>
          </Col>
          <Divider />
          <Col lg={22} xs={22}>
            <h2>Resumen de Resultado</h2>
          </Col>
          <Col lg={22} xs={22}>
            <NormalForm
              inputs={{ ...inputs, questions: [], surveys: [], measurements: [] }}
              disableForm={disableForm}
              roles={roles}
              form={form}
              status={status}
              removeDocument={removeDocument}
              convertFile={convertFile}
              convertError={convertError}
              isDICOM={isDICOM}
              handleConvertFile={handleConvertFile}
              imageUrl={imageUrl}
              setImageUrl={setImageUrl}
              countrySettings={countrySettings}
              fromExternal={fromExternal}
            />
          </Col>
        </Row>
      </Col>
    </Row>;

  return (
    <Row justify="space-between">
      {
        (from === 'consultation') &&
        <Col lg={24} xs={24} className="mb-lg-10 mb-md-10 mb-sm-10 mb-xs-10">
          {results()}
        </Col>
      }
      <Col lg={(from === 'consultation') ? 24 : 11} xs={24} className='col-questions'>
        <Row justify="center" gutter={[0, 15]}>
          <Col lg={22} xs={22}>
            <h2>Test</h2>
          </Col>
          <Col lg={22} xs={22}>
            <p className="progress-text"><strong>{progressState.toFixed(0)}% completado</strong></p>
            <Progress percent={progressState} showInfo={false} />
          </Col>
          <Col lg={22} xs={22}>
            <Table
              className='cmt-table antd-table-custom-class'
              dataSource={
                inputs?.questions
                  ?.sort((first, second) => first.order - second.order)
                  ?.sort((first, second) => first.survey_id - second.survey_id)
              }
              pagination={false}
              rowKey='id'
            >
              <Table.Column title="Item" align='left' width='25%'
                render={item => <p>
                  <Tooltip
                    key={item.order}
                    title={item.name}
                    color='#223A70'
                  >
                    <strong>{`Item ${item.order}`}</strong>
                  </Tooltip>
                </p>}
              />
              <Table.Column title="Respuesta" align='left' width='75%'
                render={item => {
                  const options = Array.isArray(item?.options) && item?.options?.map(option => ({ label: option.text, value: option.text }));
                  const name = ['questions', item.id.toString()]
                  return (
                    <>
                      {
                        ['string', 'text'].includes(item.input_type) &&
                        <Form.Item
                          className="mb-0"
                          name={name}
                          required={true}
                          {...(item.result ? { initialValue: item.result } : {
                            initialValue: item.default_value || null
                          })}
                          rules={[
                            {
                              required: true,
                              message: 'Respuesta requerida',
                            },
                          ]}
                        >
                          <Input.TextArea
                            className="cmt-input"
                            placeholder={item.default_value
                              ? item.default_value
                              : "Ingrese texto"
                            }
                            style={{ width: "100%", height: 100 }}
                            disabled={disableForm}
                            onBlur={_ => {
                              const questions = Object.values(form.getFieldsValue().questions);
                              setProgress(questions.filter(question => question).length * 100 / questions.length)
                            }}
                          />
                        </Form.Item>
                      }
                      {
                        !['string', 'text'].includes(item.input_type) &&
                        <Form.Item
                          className="mb-0"
                          name={name}
                          required={true}
                          {...(item.result && item.result.trim()
                            ? { initialValue: item.result }
                            : { initialValue: item.default_value || (options && options[0] && options[0].text) })}
                          rules={[
                            {
                              required: true,
                              message: 'Respuesta requerida',
                            },
                          ]}
                        >
                          <Select
                            className={`cmt-select ${missingState[item.id] === true && 'with-error'}`}
                            placeholder={item.default_value
                              ? item.default_value
                              : 'Seleccione puntuación'
                            }
                            disabled={disableForm}
                            options={options}
                            onInputKeyDown={e => {
                              item.options.forEach((option, index) => {
                                if (index.toString() === e.key) {
                                  form.setFields([{ name, value: option.text }])
                                }
                              });
                            }}
                            onBlur={_ => {
                              const questions = Object.values(form.getFieldsValue().questions);
                              setMissing(state => ({ ...state, [item.id]: !form.getFieldValue(name) }))
                              setProgress(questions.filter(question => question).length * 100 / questions.length)
                            }}
                          />
                        </Form.Item>
                      }
                    </>
                  )
                }}
              />
            </Table>
          </Col>
        </Row>
        <Row justify="center">
          <Col lg={21} xs={20}>
            <Row justify="end">
              <Col xl={12} lg={15} xs={24}>
                <Button
                  className="cmt-button green pr-20 pl-20 mb-50"
                  type="primary"
                  size='small'
                  icon={<ArrowUpOutlined />}
                  onClick={() => { window.scrollTo(0, 0) }}
                >
                  Ir al inicio del test
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
      {
        (from !== 'consultation') &&
        <Col lg={12} xs={24} className="mt-lg-0 mt-md-30 mt-sm-30 mt-xs-30">
          {results()}
        </Col>
      }
    </Row>
  )
};

export default PsychologyForm;
